import React, { useState, useEffect } from 'react';
import classNames from 'classnames';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import _ from 'lodash';
import { format, subHours } from 'date-fns';
import { utcToZonedTime } from 'date-fns-tz';
import { useTranslation } from 'react-i18next';
import 'react-date-range/dist/styles.css'; // main css file
import 'react-date-range/dist/theme/default.css'; // theme css file

import './DrawHistory.scss';
import { RootState } from '../../../configs/store';
import Number from '../LottoNumberPicker/Number/Number';
import Icon from 'src/components/Icon/Icon';
import RangePicker, { DateRangeProps } from 'src/components/RangePicker/RangePicker';


export default function DrawHistory() {
  const [sorted, setSorted] = useState(true);
  const [pastDraws, setPastDraws] = useState([]);
  const [showFilter, setShowFilter] = useState(false);
  const [dateRange, setDateRange] = useState<DateRangeProps[]>(
    [ {
      startDate: subHours(new Date(), 96),
      endDate: new Date(),
      key: 'selection'
    }]
  );

  const [page, setPage] = useState(0); // this will give us the number of draws that we wwant(backend multiplies it by 10)
  const [showLoadMore, setShowMore] = useState(true);
  const [hasDateChanged, setHasDateChanged] = useState(false);
  const { tournamentCode } = useParams<{ tournamentCode: string}>();
  const dispatch = useDispatch();
  const { singleNumberEvent: { numberInfos: { regular, bonus } } } = useSelector((state: RootState) => state.lotto);
  const { selectedTimezone } = useSelector((state: RootState) => state.odds);
  const { t } = useTranslation();

  const loadDraws = async () => {
    const data = await dispatch.lotto.getPreviousDraws({ tournament:tournamentCode, page });
    if (data){
      setPastDraws(data);
      if (_.size(data) < 10){
        setShowMore(false);
      }
    }
  };

  useEffect(() => {
    loadDraws();
  }, []);

  const loadMore = async (e) => {
    e.preventDefault();
    setPage(page+1);
    const moreDraws = await dispatch.lotto.getPreviousDraws({ tournament:tournamentCode, page: page + 1 });
    if (_.size(moreDraws) === _.size(pastDraws) || _.size(moreDraws)%10 !== 0){
      setShowMore(false);
    }
    setPastDraws([...pastDraws, ...moreDraws]);
  };

  const handleApply = () => {
    setShowFilter(false);
  };

  const handleClear = () => {
    setShowFilter(false);
    setHasDateChanged(false);
  };

  const onDateChange = (range:DateRangeProps[]) => {
    setDateRange(range);
    setHasDateChanged(true);
  };

  return (
    <div className="draw-history">
      <div
        className={
          classNames({
            'draw-history__filter-buttons': true,
            'draw-history__filter-buttons--open': showFilter || hasDateChanged
          })
        }
      >
        <button type="button" className="draw-history__filter-by-date" onClick={ () => setShowFilter(true) }>
          <Icon
            icon="filter"
            className="draw-history__calendar"
          />
          <span>{ t('numbers.filterByDate') }</span>
        </button>
      </div>
      <div className="draw-history__filter">
        {
          showFilter && (
            <div className="draw-history__filter-container">
              <RangePicker
                iconClick={ () => setShowFilter(false) }
                handleApply = { handleApply }
                clear = { handleClear }
                dateChange = { onDateChange }
                ranges= { dateRange }
              />
            </div>
          )
        }
      </div>
      <div className="draw-history__header">
        <div className="draw-history__draw">
          { t('numbers.draws') }
        </div>
        <div className="draw-history__results">
          { t('numbers.results') }
          <div className="draw-history__sorting" onClick={ () => setSorted(!sorted) }>
            { t('numbers.orderBy') }
            <Icon
              icon="arrow"
              className={
                classNames({
                  'draw-history__arrow': true,
                  'draw-history__arrow--up': sorted
                })
              }
            />
            <span>{ sorted ?  t('numbers.numbers') : t('numbers.draw') }</span>
          </div>
        </div>
      </div>
      {
        pastDraws && (
          _.map(pastDraws, (draw, index) => {
            const date = utcToZonedTime(draw.drawTime * 1000, selectedTimezone);

            return (
              <div className="draw-history__single-draw" key={ index }>
                <div className="draw-history__date">
                  <span>{ format(date, 'dd/MM/yyyy') }</span> <span>{ format(date, 'HH:mm') }</span>
                </div>
                <div className="draw-history__result">
                  {
                    _.map(!sorted ? draw.result : _.sortBy(draw.result), (number, index) => {
                      return (<Number
                        key={ index }
                        type="ball"
                        color={ regular?.colors[number-1]?.toLowerCase() }
                        className="draw-history"
                        value={ number }
                        bankers = { false }
                        isActive={ false }
                      />);
                    })
                  }
                </div>
                <div className="draw-history__icon-container">
                  <Icon icon="more" className="draw-history__more" />
                </div>
                <div className="draw-history__bonus">
                  {
                    _.map(draw.bonus1, (number, index) => {
                      return (<Number
                        key={ index }
                        type={ bonus?.sameBallset ? 'ball' : 'star' }
                        color={ regular?.colors[number-1]?.toLowerCase() }
                        className="draw-history"
                        value={ number }
                        bankers={ false }
                        isActive={ false }
                      />);
                    })
                  }
                </div>
              </div>
            );
          })
        )
      }
      <div className="draw-history__button-container">
        { showLoadMore && <button type="button" className="draw-history__show-more" onClick={ loadMore }>{ t('numbers.showMore') } </button> }
      </div>
    </div>
  );
}
